import { useEffect, useState } from "react";
import type { Module, Source, SourceTier, SourceType } from "@bios/types";
import { Button, Card, DataTable, Pill, type Column } from "@bios/ui";
import { useAdminApiAuthed, useAdminApiDev } from "../auth.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

const SOURCE_TYPES: SourceType[] = ["rss", "html", "api", "rss_aggregate", "filing", "press_release", "social"];
const SOURCE_TIERS: SourceTier[] = ["primary", "secondary", "tertiary", "unverified"];
const MODULES: Module[] = ["media", "capital"];

export function SourceManager({ devModeNoAuth }: PageProps) {
  const api = devModeNoAuth ? useAdminApiDev() : useAdminApiAuthed();
  const [sources, setSources] = useState<Source[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Source>>({
    module: "media",
    type: "rss",
    tier: "secondary",
    trust_weight: 0.7,
    active: 1,
  });

  const reload = () => {
    api.sources().then((r) => setSources(r.sources)).catch((e) => setError(String(e)));
  };

  useEffect(reload, [api]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy("create");
    try {
      await api.createSource(form);
      setForm({ module: form.module, type: "rss", tier: "secondary", trust_weight: 0.7, active: 1 });
      reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this source?")) return;
    setBusy(id);
    try {
      await api.deleteSource(id);
      reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  const toggleActive = async (s: Source) => {
    setBusy(s.id);
    try {
      await api.updateSource(s.id, { active: s.active === 1 ? 0 : 1 });
      reload();
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  const trigger = async (id: string) => {
    setBusy(id);
    try {
      await api.triggerSource(id);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(null);
    }
  };

  const columns: Column<Source>[] = [
    {
      key: "name",
      header: "Source",
      render: (s) => (
        <div>
          <div style={{ fontWeight: 500 }}>{s.name}</div>
          <div style={{ fontSize: 11, color: "var(--bios-text-faint)" }}>{s.url}</div>
        </div>
      ),
    },
    { key: "module", header: "Module", render: (s) => <Pill>{s.module}</Pill> },
    { key: "type", header: "Type", render: (s) => <Pill>{s.type}</Pill> },
    { key: "tier", header: "Tier", render: (s) => <Pill variant={s.tier === "primary" ? "signal" : s.tier === "unverified" ? "noise" : "default"}>{s.tier}</Pill> },
    {
      key: "active",
      header: "Active",
      render: (s) => (
        <Pill variant={s.active ? "signal" : "noise"}>{s.active ? "on" : "off"}</Pill>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (s) => (
        <div style={{ display: "flex", gap: 6 }}>
          <Button onClick={() => toggleActive(s)} disabled={busy === s.id}>
            {s.active ? "Disable" : "Enable"}
          </Button>
          <Button onClick={() => trigger(s.id)} disabled={busy === s.id}>Trigger</Button>
          <Button onClick={() => remove(s.id)} disabled={busy === s.id}>Delete</Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <h1 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: "0 0 6px" }}>Source registry</h1>
      <p style={{ color: "var(--bios-text-muted)", fontSize: 13, margin: "0 0 24px" }}>
        Add, edit, enable, and trigger sources. Sources marked active are pulled by the ingestion worker.
      </p>

      {error && (
        <Card>
          <p style={{ color: "var(--bios-noise)", fontSize: 13 }}>{error}</p>
        </Card>
      )}

      <div className="bios-grid cols-2" style={{ marginBottom: 16 }}>
        <Card title="Add source">
          <form onSubmit={submit} className="bios-form-grid">
            <label>Name</label>
            <input className="bios-input" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <label>URL</label>
            <input className="bios-input" type="url" value={form.url ?? ""} onChange={(e) => setForm({ ...form, url: e.target.value })} required />
            <label>Module</label>
            <select className="bios-input" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value as Module })}>
              {MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <label>Type</label>
            <select className="bios-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as SourceType })}>
              {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label>Tier</label>
            <select className="bios-input" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value as SourceTier })}>
              {SOURCE_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label>Trust weight</label>
            <input
              className="bios-input"
              type="number"
              step="0.05"
              min={0}
              max={1}
              value={form.trust_weight ?? 0.7}
              onChange={(e) => setForm({ ...form, trust_weight: Number(e.target.value) })}
            />
            <div />
            <Button variant="primary" type="submit" disabled={busy === "create"}>
              {busy === "create" ? "Saving…" : "Add source"}
            </Button>
          </form>
        </Card>

        <Card title="Pipeline reference">
          <p style={{ fontSize: 13, color: "var(--bios-text-muted)", lineHeight: 1.6 }}>
            <strong>Source → Ingestion → Queue → Classification → Scoring → Storage → Dashboard</strong>
          </p>
          <ul style={{ fontSize: 12, color: "var(--bios-text-muted)", lineHeight: 1.7, paddingLeft: 18 }}>
            <li>Ingestion worker runs on cron + manual triggers</li>
            <li>Items pass dedupe + low-quality filter</li>
            <li>Classification: activity_type, topics, entities</li>
            <li>Reality Score blends activity, evidence, novelty, source trust</li>
          </ul>
        </Card>
      </div>

      <Card title="All sources">
        <DataTable columns={columns} rows={sources} rowKey={(s) => s.id} empty="No sources yet." />
      </Card>
    </>
  );
}
